/* todomamás · theme.js
   Carrito lateral (Section Rendering API), formularios de producto,
   menú de escritorio y menú móvil. Sin dependencias. */
(() => {
  const theme = window.theme || {};
  const CART_SECTION = 'cart-drawer';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- Utilidades ---------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || theme.strings?.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  /* ---------- Contador de la cabecera ---------- */
  const updateCount = (count) => {
    document.querySelectorAll('[data-carrito-contador]').forEach((badge) => {
      const visible = badge.querySelector('[aria-hidden]');
      if (visible) visible.textContent = count;
      badge.hidden = Number(count) === 0;
    });
  };

  /* ---------- Carrito lateral ---------- */
  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.carrito-lateral__panel');
      this.lastTrigger = null;
      this.onKeydown = this.onKeydown.bind(this);

      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-carrito-cerrar]')) {
          this.close();
          return;
        }
        const change = event.target.closest('[data-carrito-cambiar]');
        if (change) {
          this.change(change.dataset.linea, change.dataset.cantidad);
        }
      });

      root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-carrito-input]');
        if (!input) return;
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.change(input.dataset.linea, quantity);
      });
    }

    get content() {
      return this.root.querySelector('#CarritoLateral-contenido');
    }

    open(trigger) {
      this.lastTrigger = trigger || document.activeElement;
      this.root.removeAttribute('inert');
      this.root.classList.add('is-abierto');
      document.body.classList.add('bloqueo-scroll');
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => this.panel.focus());
      this.loadRecommendations();
    }

    close() {
      this.root.classList.remove('is-abierto');
      this.root.setAttribute('inert', '');
      document.body.classList.remove('bloqueo-scroll');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastTrigger && document.contains(this.lastTrigger)) this.lastTrigger.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    render(sections) {
      const html = sections && sections[CART_SECTION];
      if (!html) return;
      const fresh = parseHTML(html).querySelector('#CarritoLateral-contenido');
      if (!fresh) return;
      this.content.querySelectorAll('[data-resenas]').forEach((c) => clearInterval(c.__intervalo));
      /* La barra de envío gratis crece desde donde estaba, no desde cero. */
      const antes = this.content.querySelector('[data-envio-gratis]')?.dataset.progreso;
      const nuevaBarra = fresh.querySelector('[data-envio-gratis]');
      if (nuevaBarra && antes !== undefined) {
        nuevaBarra.style.setProperty('--progreso-antes', `${antes}%`);
        if (Number(antes) < 100 && Number(nuevaBarra.dataset.progreso) >= 100) nuevaBarra.classList.add('is-celebrando');
      }
      this.content.replaceWith(fresh);
      updateCount(fresh.dataset.contador);
      fresh.querySelectorAll('[data-resenas]').forEach(rotarResenas);
      if (this.root.classList.contains('is-abierto')) this.loadRecommendations();
    }

    showError(message) {
      const box = this.root.querySelector('[data-carrito-error]');
      if (!box) return;
      box.textContent = message || theme.strings?.cartError;
      box.hidden = false;
    }

    async change(line, quantity) {
      this.root.classList.add('is-cargando');
      try {
        const data = await postJSON(`${theme.routes.cartChange}.js`, {
          line: Number(line),
          quantity: Number(quantity),
          sections: [CART_SECTION],
          sections_url: window.location.pathname
        });
        this.render(data.sections);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.root.classList.remove('is-cargando');
      }
    }

    async loadRecommendations() {
      if (!theme.showRecommendations) return;
      const slot = this.root.querySelector('[data-recomendaciones]');
      if (!slot || !slot.dataset.productosIds) return;
      const clave = slot.dataset.clave;
      this.cacheRecos = this.cacheRecos || new Map();

      /* Ya calculadas para este mismo carrito: se vuelven a poner al momento
         (antes desaparecían al cambiar una cantidad). */
      if (this.cacheRecos.has(clave)) {
        const guardado = this.cacheRecos.get(clave);
        if (guardado) slot.replaceChildren(parseHTML(guardado).querySelector('[data-recos-total]'));
        return;
      }
      if (this.recosPendiente === clave) return;
      this.recosPendiente = clave;

      const pedir = async (productId, intent) => {
        const url = `${theme.routes.recommendations}?section_id=cart-recommendations&product_id=${productId}&limit=10&intent=${intent}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const doc = parseHTML(await response.text());
        const block = doc.querySelector('[data-recos-total]');
        return block && Number(block.dataset.recosTotal) > 0 ? block : null;
      };

      try {
        /* Cada producto del carrito, del último añadido al primero:
           primero sus complementarios y, si no hay, sus relacionados. */
        let block = null;
        for (const productId of slot.dataset.productosIds.split(',').filter(Boolean)) {
          block = (await pedir(productId, 'complementary')) || (await pedir(productId, 'related'));
          if (block) break;
        }
        this.cacheRecos.set(clave, block ? block.outerHTML : null);
        const currentSlot = this.root.querySelector('[data-recomendaciones]');
        if (block && currentSlot && currentSlot.dataset.clave === clave) currentSlot.replaceChildren(block);
      } catch (error) {
        /* Las recomendaciones son opcionales: si fallan, el carrito sigue funcionando. */
      } finally {
        if (this.recosPendiente === clave) this.recosPendiente = null;
      }
    }
  }

  const drawerRoot = document.querySelector('[data-carrito-lateral]');
  const drawer = drawerRoot && theme.cartType === 'drawer' ? new CartDrawer(drawerRoot) : null;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-carrito-abrir]');
    if (!trigger || !drawer) return;
    event.preventDefault();
    drawer.open(trigger);
  });

  /* ---------- Formularios de producto (añadir al carrito) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-formulario-producto]');
    if (!form || !drawer) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const errorBox = form.querySelector('[data-formulario-error]');
    if (button?.getAttribute('aria-disabled') === 'true') return;

    const formData = new FormData(form);
    const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };

    if (errorBox) errorBox.hidden = true;
    button?.setAttribute('aria-busy', 'true');

    try {
      const data = await postJSON(`${theme.routes.cartAdd}.js`, {
        items: [item],
        sections: [CART_SECTION],
        sections_url: window.location.pathname
      });
      drawer.render(data.sections);
      drawer.open(button);
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } else {
        drawer.showError(error.message);
      }
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Añadir rápido (tarjetas y «Combina con») ----------
     Botón «Añadir a tu carrito» → sube un panel con muestras de color y
     botones de talla → al completar la combinación se añade y se abre el
     carrito. Con una sola variante, añade al primer toque. */
  let panelRapido = null;

  const anadirVariante = async (root, variantId, trigger) => {
    const errorBox = root.querySelector('[data-anadir-rapido-error]');
    if (!drawer) {
      const form = root.querySelector('form');
      const input = form?.querySelector('[data-anadir-rapido-id]');
      if (input) input.value = variantId;
      form?.submit();
      return false;
    }
    if (errorBox) errorBox.hidden = true;
    root.classList.add('is-cargando');
    trigger?.setAttribute('aria-busy', 'true');
    try {
      const data = await postJSON(`${theme.routes.cartAdd}.js`, {
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: [CART_SECTION],
        sections_url: window.location.pathname
      });
      drawer.render(data.sections);
      drawer.open(document.contains(trigger) ? trigger : null);
      return true;
    } catch (error) {
      const compacto = root.classList.contains('anadir-rapido--compacto');
      if (panelRapido && document.contains(panelRapido)) {
        const aviso = panelRapido.querySelector('[data-selector-aviso]');
        if (aviso) aviso.textContent = error.message;
      } else if (errorBox && document.contains(errorBox) && !compacto) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } else {
        drawer.showError(error.message);
      }
      return false;
    } finally {
      root.classList.remove('is-cargando');
      trigger?.removeAttribute('aria-busy');
    }
  };

  const cerrarPanelRapido = (devolverFoco = false) => {
    if (!panelRapido) return;
    const panel = panelRapido;
    const root = panel.__root;
    panelRapido = null;
    root?.querySelector('[data-anadir-rapido-abrir]')?.setAttribute('aria-expanded', 'false');
    root?.classList.remove('is-abierto');
    panel.closest('.tarjeta')?.classList.remove('tiene-selector');
    panel.classList.add('is-saliendo');
    setTimeout(() => panel.remove(), 180);
    if (devolverFoco && root && document.contains(root)) root.querySelector('[data-anadir-rapido-abrir]')?.focus();
  };

  const abrirPanelRapido = (root, boton) => {
    cerrarPanelRapido();
    const plantilla = root.querySelector('[data-anadir-rapido-plantilla]');
    const json = root.querySelector('[data-anadir-rapido-json]');
    if (!plantilla || !json) return;

    const variantes = JSON.parse(json.textContent);
    const panel = plantilla.content.firstElementChild.cloneNode(true);
    const grupos = [...panel.querySelectorAll('[data-selector-grupo]')];
    const total = grupos.length;
    const eleccion = new Array(total).fill(null);

    /* Se deja elegido todo menos la última opción (normalmente la talla),
       tomando la primera combinación con stock. Así basta un toque. */
    const primera = variantes.find((v) => v.available) || variantes[0];
    grupos.forEach((grupo, i) => {
      const valores = grupo.querySelectorAll('[data-selector-valor]');
      if (valores.length === 1) eleccion[i] = valores[0].dataset.selectorValor;
      else if (i < total - 1 && primera) eleccion[i] = primera.options[i];
    });

    const aviso = panel.querySelector('[data-selector-aviso]');
    const precio = panel.querySelector('[data-selector-precio]');
    const tarjeta = root.closest('.tarjeta');
    const imagenTarjeta = tarjeta?.querySelector('.tarjeta__imagen');

    const disponible = (i, valor) =>
      variantes.some(
        (v) => v.available && v.options[i] === valor && v.options.every((o, j) => j === i || eleccion[j] === null || o === eleccion[j])
      );

    const pintar = () => {
      grupos.forEach((grupo, i) => {
        const elegido = grupo.querySelector('[data-selector-elegido]');
        if (elegido) elegido.textContent = eleccion[i] ? `: ${eleccion[i]}` : '';
        grupo.querySelectorAll('[data-selector-valor]').forEach((b) => {
          const valor = b.dataset.selectorValor;
          b.setAttribute('aria-pressed', String(eleccion[i] === valor));
          b.classList.toggle('is-agotado', !disponible(i, valor));
        });
      });
      const exacta = eleccion.every((e) => e !== null) && variantes.find((v) => v.options.every((o, j) => o === eleccion[j]));
      const orientativa = exacta || variantes.find((v) => v.options.every((o, j) => eleccion[j] === null || o === eleccion[j]));
      if (precio && orientativa) precio.textContent = orientativa.price;
    };

    panel.addEventListener('click', async (event) => {
      if (event.target.closest('[data-selector-cerrar]')) {
        cerrarPanelRapido(true);
        return;
      }
      const b = event.target.closest('[data-selector-valor]');
      if (!b || panel.classList.contains('is-cargando')) return;
      const i = Number(b.closest('[data-selector-grupo]').dataset.selectorGrupo);
      eleccion[i] = b.dataset.selectorValor;
      if (aviso) aviso.textContent = '';

      /* Si al cambiar de color la talla ya no existe, se deja sin elegir. */
      eleccion.forEach((valor, j) => {
        if (j !== i && valor !== null && !disponible(j, valor)) eleccion[j] = null;
      });

      if (b.dataset.selectorFoto && imagenTarjeta) {
        imagenTarjeta.removeAttribute('srcset');
        imagenTarjeta.src = b.dataset.selectorFoto;
      }
      pintar();

      if (eleccion.some((e) => e === null)) return;
      const variante = variantes.find((v) => v.options.every((o, j) => o === eleccion[j]));
      if (!variante || !variante.available) {
        if (aviso) aviso.textContent = theme.strings.quickUnavailable || '';
        return;
      }
      panel.classList.add('is-cargando');
      b.classList.add('is-anadiendo');
      const ok = await anadirVariante(root, variante.id, b);
      if (!document.contains(panel)) return;
      panel.classList.remove('is-cargando');
      b.classList.remove('is-anadiendo');
      if (ok) {
        panel.classList.add('is-hecho');
        if (aviso) aviso.textContent = theme.strings.quickAdded || '';
        setTimeout(() => { if (panelRapido === panel) cerrarPanelRapido(); }, 700);
      }
    });

    /* Dónde se coloca: en el carrito, bajo la fila de «Combina con»; en las
       tarjetas, sube desde abajo cubriendo la parte baja de la tarjeta. */
    const destino = root.closest('.recos')?.querySelector('[data-recos-panel]') || tarjeta || root;
    panel.__root = root;
    destino.appendChild(panel);
    tarjeta?.classList.add('tiene-selector');
    panelRapido = panel;
    root.classList.add('is-abierto');
    boton?.setAttribute('aria-expanded', 'true');
    pintar();
    requestAnimationFrame(() => {
      panel.classList.add('is-visible');
      const primerLibre = panel.querySelector('[data-selector-grupo]:last-of-type [data-selector-valor]:not(.is-agotado)');
      (primerLibre || panel).focus({ preventScroll: true });
      if (panel.closest('.carrito-lateral')) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  document.addEventListener('click', (event) => {
    const directo = event.target.closest('[data-anadir-rapido-directo]');
    if (directo) {
      event.preventDefault();
      const root = directo.closest('[data-anadir-rapido]');
      const id = root?.querySelector('[data-anadir-rapido-id]')?.value;
      if (root && id) anadirVariante(root, id, directo);
      return;
    }

    const abrir = event.target.closest('[data-anadir-rapido-abrir]');
    if (abrir) {
      event.preventDefault();
      const root = abrir.closest('[data-anadir-rapido]');
      if (!root) return;
      if (panelRapido && panelRapido.__root === root) cerrarPanelRapido();
      else abrirPanelRapido(root, abrir);
      return;
    }

    /* Clic fuera del panel: se cierra. */
    if (panelRapido && !event.target.closest('.selector-rapido') && !event.target.closest('[data-anadir-rapido]')) {
      cerrarPanelRapido();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panelRapido) {
      event.stopPropagation();
      cerrarPanelRapido(true);
    }
  }, true);

  /* ---------- Carruseles (fotos de clientas y reseñas) ----------
     Las flechas mueven la fila un ancho de pantalla, respetando dónde
     encaja cada tarjeta, y se apagan al llegar a los extremos. */
  document.querySelectorAll('[data-carrusel]').forEach((carrusel) => {
    const pista = carrusel.querySelector('[data-carrusel-pista]');
    const anterior = carrusel.querySelector('[data-carrusel-anterior]');
    const siguiente = carrusel.querySelector('[data-carrusel-siguiente]');
    if (!pista) return;

    const paso = () => {
      const primera = pista.firstElementChild;
      if (!primera) return pista.clientWidth;
      const estilo = getComputedStyle(pista);
      const hueco = parseFloat(estilo.columnGap || estilo.gap) || 0;
      const ancho = primera.getBoundingClientRect().width + hueco;
      /* Tantas tarjetas enteras como quepan, al menos una. */
      const caben = Math.max(1, Math.floor(pista.clientWidth / ancho));
      return ancho * caben;
    };

    const refrescar = () => {
      const margen = 4;
      const maximo = pista.scrollWidth - pista.clientWidth - margen;
      const alPrincipio = pista.scrollLeft <= margen;
      const alFinal = pista.scrollLeft >= maximo;
      const sinDesplazamiento = maximo <= margen;
      if (anterior) anterior.disabled = alPrincipio || sinDesplazamiento;
      if (siguiente) siguiente.disabled = alFinal || sinDesplazamiento;
      carrusel.classList.toggle('carrusel--sin-flechas', sinDesplazamiento);
    };

    const mover = (sentido) => {
      pista.scrollBy({ left: paso() * sentido, behavior: 'smooth' });
    };

    anterior?.addEventListener('click', () => mover(-1));
    siguiente?.addEventListener('click', () => mover(1));
    pista.addEventListener('scroll', () => {
      clearTimeout(pista.__temporizador);
      pista.__temporizador = setTimeout(refrescar, 80);
    }, { passive: true });
    window.addEventListener('resize', refrescar);
    /* Las fotos cambian el ancho al cargar, así que se vuelve a mirar. */
    window.addEventListener('load', refrescar);
    refrescar();
  });

  /* ---------- Barra de compra fija ----------
     Sale cuando el bloque de compra queda por encima de la pantalla y se
     esconde al volver a verlo. Se mueve al <body> para que ningún
     contenedor la recorte ni le cambie la posición en el móvil.

     Compatible con apps de combos o packs (Pumper Bundles y similares):
     esas apps esconden el botón del tema y ponen el suyo. Un botón
     escondido mide cero y la barra no aparecía nunca. Por eso se busca el
     primer punto de referencia que de verdad ocupe sitio en la página. */
  let barraCompra = null;
  let barraPendiente = false;

  const PUNTOS_REFERENCIA = [
    '[data-producto-info] [data-boton-anadir]',
    '[data-producto-info] .producto__compra',
    '[data-producto-info] form[action*="/cart/add"]',
    '[data-producto-info] [data-producto-precio]',
    '[data-producto-info]'
  ];

  const visibleEnPagina = (el) => {
    if (!el || el.closest('[data-barra-compra]')) return false;
    const caja = el.getBoundingClientRect();
    return caja.height > 0 && caja.width > 0;
  };

  const referenciaBarra = () => {
    for (const selector of PUNTOS_REFERENCIA) {
      for (const el of document.querySelectorAll(selector)) {
        if (visibleEnPagina(el)) return el.getBoundingClientRect();
      }
    }
    return null;
  };

  const revisarBarraCompra = () => {
    barraPendiente = false;
    if (!barraCompra || !document.contains(barraCompra)) return;
    const caja = referenciaBarra();
    const alto = window.innerHeight || document.documentElement.clientHeight;
    /* Sin referencia (app que lo reemplaza todo): con haber bajado una
       pantalla ya se enseña, que es lo que se espera. */
    const pasoDeLargo = caja ? caja.bottom < 0 : window.scrollY > alto;
    /* Se esconde cerca del pie para no tapar los enlaces legales. */
    const pie = document.querySelector('.pie');
    const enPie = pie ? pie.getBoundingClientRect().top < alto * 0.6 : false;
    const visible = pasoDeLargo && !enPie;
    const yaVisible = barraCompra.classList.contains('is-visible');
    if (visible === yaVisible) return;
    barraCompra.classList.toggle('is-visible', visible);
    if (visible) barraCompra.removeAttribute('inert');
    else barraCompra.setAttribute('inert', '');
    document.body.classList.toggle('con-barra-compra', visible);
    document.documentElement.style.setProperty('--barra-compra-alto', `${barraCompra.offsetHeight}px`);
  };

  const pedirRevisionBarra = () => {
    if (barraPendiente) return;
    barraPendiente = true;
    requestAnimationFrame(revisarBarraCompra);
  };

  function actualizarBarraCompra() {
    const nueva = document.querySelector('[data-producto-info] [data-barra-compra]');
    if (nueva) {
      const estabaVisible = barraCompra?.classList.contains('is-visible');
      document.querySelectorAll('body > [data-barra-compra]').forEach((vieja) => vieja.remove());
      document.body.appendChild(nueva);
      barraCompra = nueva;
      if (estabaVisible) {
        /* Tras cambiar de talla, la barra nueva aparece ya en su sitio, sin volver a subir. */
        barraCompra.style.transition = 'none';
        barraCompra.classList.remove('is-visible');
        revisarBarraCompra();
        requestAnimationFrame(() => { barraCompra.style.transition = ''; });
        if (typeof actualizarBarraCombo === 'function') actualizarBarraCombo();
        return;
      }
    } else if (!barraCompra) {
      barraCompra = document.querySelector('body > [data-barra-compra]');
    }
    barraCompra?.classList.remove('is-visible');
    revisarBarraCompra();
    if (typeof actualizarBarraCombo === 'function') actualizarBarraCombo();
  }

  /* El botón de la barra fija repite el clic del botón real de la ficha.
     Si una app de combos ha puesto el suyo, se pulsa el de la app, para que
     el pack y su descuento entren en el carrito tal y como los ha elegido
     la clienta. Solo si no hay ninguno, la barra envía su propio formulario. */
  const botonRealAnadir = () => {
    const candidatos = [
      '[data-producto-info] [data-boton-anadir]',
      '[data-producto-info] form[action*="/cart/add"] button[type="submit"]',
      '[data-producto-info] form[action*="/cart/add"] [name="add"]',
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] [name="add"]'
    ];
    for (const selector of candidatos) {
      for (const el of document.querySelectorAll(selector)) {
        if (!visibleEnPagina(el)) continue;
        if (el.disabled || el.getAttribute('aria-disabled') === 'true') continue;
        return el;
      }
    }
    return null;
  };

  document.addEventListener('click', (event) => {
    const botonFijo = event.target.closest('[data-boton-fijo]');
    if (!botonFijo) return;
    const real = botonRealAnadir();
    if (!real) return;
    event.preventDefault();
    real.click();
  });

  if (document.querySelector('[data-barra-compra]')) actualizarBarraCompra();
  window.addEventListener('scroll', pedirRevisionBarra, { passive: true });
  window.addEventListener('resize', pedirRevisionBarra);
  window.addEventListener('orientationchange', pedirRevisionBarra);
  /* Las apps de combos y packs montan su bloque un poco después de cargar la
     página: cuando cambia el alto, se vuelve a mirar dónde está el botón. */
  if ('ResizeObserver' in window) {
    const info = document.querySelector('[data-producto-info]');
    if (info) new ResizeObserver(pedirRevisionBarra).observe(info);
  }
  window.addEventListener('load', pedirRevisionBarra);
  /* En Personalizar, al tocar un ajuste la sección se vuelve a pintar. */
  document.addEventListener('shopify:section:load', () => {
    if (document.querySelector('[data-producto-info] [data-barra-compra]')) actualizarBarraCompra();
  });

  /* ---------- Panel lateral genérico (guía de tallas y filtros en móvil) ---------- */
  const abrirPanel = (root, trigger) => {
    root.__trigger = trigger || document.activeElement;
    root.removeAttribute('inert');
    root.classList.add('is-abierto');
    document.body.classList.add('bloqueo-scroll');
    const panel = root.querySelector('[role="dialog"]');
    requestAnimationFrame(() => panel?.focus());
    root.__onKey = (event) => {
      if (event.key === 'Escape') {
        cerrarPanel(root);
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', root.__onKey);
  };

  const cerrarPanel = (root) => {
    if (!root) return;
    root.classList.remove('is-abierto');
    root.setAttribute('inert', '');
    document.body.classList.remove('bloqueo-scroll');
    if (root.__onKey) document.removeEventListener('keydown', root.__onKey);
    if (root.__trigger && document.contains(root.__trigger)) root.__trigger.focus();
  };

  /* ---------- Guía de tallas ---------- */
  document.addEventListener('click', (event) => {
    const abrir = event.target.closest('[data-guia-abrir]');
    if (abrir) {
      const panel = document.getElementById(abrir.getAttribute('aria-controls'));
      if (panel) abrirPanel(panel, abrir);
      return;
    }
    const cerrar = event.target.closest('[data-guia-cerrar]');
    if (cerrar) cerrarPanel(cerrar.closest('[data-guia]'));
  });

  /* ---------- Cantidad de la ficha ---------- */
  document.addEventListener('click', (event) => {
    const paso = event.target.closest('[data-cantidad-paso]');
    if (!paso) return;
    const input = paso.closest('.cantidad')?.querySelector('[data-cantidad-input]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(paso.dataset.cantidadPaso));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Galería de la ficha ---------- */

  /* Deja a la vista solo las fotos del color elegido. Se guía por el texto
     alternativo: valen «verde», «verde-01», «verde 02»… Si ninguna coincide,
     se muestran todas, para no dejar la galería vacía. */
  const filtrarGaleriaPorColor = (color) => {
    const pista = document.querySelector('[data-galeria-slides]');
    if (!pista) return;
    const slides = [...pista.querySelectorAll('[data-media-id]')];
    const miniaturas = [...document.querySelectorAll('[data-ir-a-media]')];
    if (!slides.length) return;

    const coincide = (alt) => {
      if (!color || !alt) return false;
      const limpio = alt.trim();
      return limpio === color || limpio.startsWith(`${color}-`) || limpio.startsWith(`${color} `) || limpio.startsWith(`${color}_`);
    };

    const hayCoincidencias = slides.some((s) => coincide(s.dataset.alt));

    slides.forEach((slide) => {
      const visible = !hayCoincidencias || coincide(slide.dataset.alt);
      slide.hidden = !visible;
    });
    miniaturas.forEach((boton) => {
      const li = boton.closest('li') || boton;
      const visible = !hayCoincidencias || coincide(boton.dataset.alt);
      li.hidden = !visible;
    });

    /* Deja la primera foto visible como la que se ve. */
    const primera = slides.find((s) => !s.hidden);
    if (primera) {
      pista.scrollTo({ left: primera.offsetLeft - pista.offsetLeft, behavior: 'auto' });
      miniaturas.forEach((b) => {
        if (b.dataset.irAMedia === primera.dataset.mediaId) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
    }
  };

  const colorElegido = () => document.querySelector('.variantes__radio[data-color]:checked')?.dataset.color || '';

  const galeria = document.querySelector('[data-galeria-slides]');
  if (galeria) {
    filtrarGaleriaPorColor(colorElegido());
    const miniaturas = [...document.querySelectorAll('[data-ir-a-media]')];

    const marcar = (id) => {
      miniaturas.forEach((boton) => {
        if (boton.dataset.irAMedia === String(id)) boton.setAttribute('aria-current', 'true');
        else boton.removeAttribute('aria-current');
      });
    };

    miniaturas.forEach((boton) => {
      boton.addEventListener('click', () => {
        const slide = galeria.querySelector(`[data-media-id="${boton.dataset.irAMedia}"]`);
        if (!slide) return;
        galeria.scrollTo({ left: slide.offsetLeft - galeria.offsetLeft, behavior: 'smooth' });
        marcar(boton.dataset.irAMedia);
      });
    });

    if (miniaturas.length && 'IntersectionObserver' in window) {
      const observador = new IntersectionObserver(
        (entradas) => {
          entradas.forEach((entrada) => {
            if (entrada.isIntersecting) marcar(entrada.target.dataset.mediaId);
          });
        },
        { root: galeria, threshold: 0.6 }
      );
      galeria.querySelectorAll('[data-media-id]').forEach((slide) => observador.observe(slide));
    }
  }

  /* ---------- Selector de variantes ---------- */
  /* ---------- Compatibilidad con Pumper Bundles ----------
     Los combos de Pumper llevan dentro el producto que se está viendo con sus
     propios desplegables de color y talla, y arrancan en la variante por
     defecto. Aquí se ponen en la variante que la clienta ha elegido en la
     ficha: al cambiar de variante, al abrir un combo y cuando la app termina
     de cargar. Solo se toca la fila de ESTE producto (se reconoce por su
     nombre); el resto de prendas del combo se quedan como estén. */
  const normalizarTexto = (t) => String(t || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let varianteActual = null;

  const sincronizarCombos = () => {
    const variante = varianteActual;
    if (!variante) return;
    const titulo = normalizarTexto(document.querySelector('[data-producto-info] h1')?.textContent).slice(0, 10);
    if (!titulo) return;
    const objetivos = variante.options.map(normalizarTexto);

    /* Miniaturas de ESTE producto dentro de los combos (también en los combos
       cerrados, que es lo primero que ve la clienta): foto de la variante
       elegida en vez de la de por defecto. */
    if (variante.imagen) {
      document.querySelectorAll('.pmpr_tier_list img').forEach((img) => {
        let fila = img.parentElement;
        for (let i = 0; i < 5 && fila && normalizarTexto(fila.textContent).length < 15; i += 1) fila = fila.parentElement;
        if (!fila || !normalizarTexto(fila.textContent).includes(titulo)) return;
        /* Si en la fila hay más de un producto, no es la fila de uno solo. */
        if (fila.querySelectorAll('img').length > 1) return;
        if (img.getAttribute('src') === variante.imagen) return;
        img.removeAttribute('srcset');
        img.setAttribute('src', variante.imagen);
      });
    }

    document.querySelectorAll('.pmpr_optsel_group').forEach((grupo) => {
      /* La fila del producto es el primer contenedor con foto. */
      let fila = grupo.parentElement;
      while (fila && !fila.querySelector('img') && !fila.classList.contains('pmpr_tier_list')) fila = fila.parentElement;
      if (!fila || fila.classList.contains('pmpr_tier_list')) return;
      if (!normalizarTexto(fila.textContent).includes(titulo)) return;

      const actual = normalizarTexto(grupo.querySelector('.pmpr_optsel_value')?.textContent);
      const opcion = [...grupo.querySelectorAll('.pmpr_optsel_option')].find((o) => objetivos.includes(normalizarTexto(o.textContent)));
      if (!opcion || normalizarTexto(opcion.textContent) === actual) return;
      grupo.querySelector('.pmpr_optsel_trigger')?.click();
      opcion.click();
    });
  };

  /* La barra fija enseña el combo elegido (nombre y precio del pack) en vez
     del precio de la prenda sola. Con el primer combo («Sin combo») vuelve a
     enseñar la variante y su precio. */
  function escaparHTML(t) {
    return String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* Declarada como función para poder llamarla desde la barra fija, que se
     prepara antes en el archivo. */
  function actualizarBarraCombo() {
    const barra = document.querySelector('body > [data-barra-compra]') || document.querySelector('[data-barra-compra]');
    if (!barra) return;
    const precioEl = barra.querySelector('.barra-compra__precio');
    const varianteEl = barra.querySelector('.barra-compra__variante');
    if (precioEl && precioEl.dataset.original === undefined) precioEl.dataset.original = precioEl.innerHTML;
    if (varianteEl && varianteEl.dataset.original === undefined) varianteEl.dataset.original = varianteEl.textContent;

    const tarjetas = [...document.querySelectorAll('.pmpr-tier-shell__card')];
    const elegida = tarjetas.find((t) => t.dataset.selected === 'true');
    if (!elegida || elegida === tarjetas[0]) {
      if (precioEl && precioEl.dataset.original !== undefined) precioEl.innerHTML = precioEl.dataset.original;
      if (varianteEl && varianteEl.dataset.original !== undefined) varianteEl.textContent = varianteEl.dataset.original;
      return;
    }

    /* Precio del combo: el primer importe de la tarjeta que no es una
       etiqueta de descuento; el tachado es el precio sin combo. */
    const importes = [...elegida.querySelectorAll('*')].filter(
      (el) => !el.children.length && /\d/.test(el.textContent) && /[€$]|EUR|MXN|CLP/.test(el.textContent) && !el.closest('.pmpr-tier-label')
    );
    const total = importes.find((el) => el.tagName !== 'S');
    const antes = importes.find((el) => el.tagName === 'S');
    if (total && precioEl) {
      precioEl.innerHTML = `<div class="precio${antes ? ' precio--oferta' : ''}"><span class="precio__ahora">${escaparHTML(total.textContent.trim())}</span>${antes ? `<s class="precio__antes">${escaparHTML(antes.textContent.trim())}</s>` : ''}</div>`;
    }
    const nombre = elegida.querySelector('.pmpr-tier-title')?.textContent.trim();
    if (varianteEl && nombre) varianteEl.textContent = nombre;
  }

  /* Cuando la app cambia el combo marcado, se actualiza la barra. */
  const vigilarComboElegido = () => {
    const info = document.querySelector('[data-producto-info]');
    if (!info || !('MutationObserver' in window)) return;
    new MutationObserver((cambios) => {
      if (cambios.some((c) => c.attributeName === 'data-selected' || c.type === 'childList')) {
        clearTimeout(vigilarComboElegido.temporizador);
        vigilarComboElegido.temporizador = setTimeout(actualizarBarraCombo, 80);
      }
    }).observe(info, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-selected'] });
  };

  /* Se repite un par de veces porque la app redibuja con un pequeño retraso. */
  const pedirSincronizarCombos = () => {
    [60, 400, 1200].forEach((espera) => setTimeout(sincronizarCombos, espera));
  };

  /* Al elegir otro combo (clic en su tarjeta, no en sus desplegables). */
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.pmpr_tier_list')) return;
    if (event.target.closest('.pmpr_optsel_group')) return;
    pedirSincronizarCombos();
  });

  /* Cuando la app monta sus combos por primera vez. */
  const vigilarMontajeCombos = () => {
    const info = document.querySelector('[data-producto-info]');
    if (!info) return;
    /* Si la app ya estaba montada cuando llega el tema, se sincroniza ya. */
    if (document.querySelector('.pmpr_optsel_group')) {
      pedirSincronizarCombos();
      return;
    }
    if (!('MutationObserver' in window)) return;
    let hecho = false;
    const observador = new MutationObserver(() => {
      if (hecho || !document.querySelector('.pmpr_optsel_group')) return;
      hecho = true;
      observador.disconnect();
      pedirSincronizarCombos();
    });
    observador.observe(info, { childList: true, subtree: true });
    setTimeout(() => observador.disconnect(), 15000);
  };

  /* Avisa a las apps de que ha cambiado la variante, por todas las vías
     habituales: el campo «id» de los formularios de la ficha, la URL y
     eventos con los nombres que usan los temas de Shopify. */
  const avisarCambioVariante = (info, variante) => {
    varianteActual = variante;
    document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((campo) => {
      if (campo.closest('[data-anadir-rapido]') || campo.closest('.carrito-lateral')) return;
      if (campo.closest('[data-barra-compra]') || (info && info.contains(campo))) {
        campo.value = variante.id;
        campo.dispatchEvent(new Event('input', { bubbles: true }));
        campo.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    const detalle = { variant: variante, variantId: variante.id, id: variante.id };
    ['variant:change', 'variant:changed', 'variantChange', 'theme:variant:change', 'product:variant-change'].forEach((nombre) => {
      document.dispatchEvent(new CustomEvent(nombre, { detail: detalle, bubbles: true }));
    });
    pedirSincronizarCombos();
  };

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variantes-json]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.addEventListener('change', () => this.onChange());
      this.marcarNoDisponibles();
      /* Variante con la que arranca la ficha, para los combos de las apps. */
      varianteActual = this.variantePara(this.seleccion) || varianteActual;
      if (this.closest('[data-producto-info]')) {
        vigilarMontajeCombos();
        vigilarComboElegido();
      }
    }

    get seleccion() {
      return [...this.querySelectorAll('.variantes__radio:checked')]
        .sort((a, b) => Number(a.dataset.indice) - Number(b.dataset.indice))
        .map((input) => input.value);
    }

    variantePara(valores) {
      return this.variants.find((v) => v.options.every((opcion, i) => opcion === valores[i]));
    }

    marcarNoDisponibles() {
      const valores = this.seleccion;
      this.querySelectorAll('.variantes__radio').forEach((input) => {
        const indice = Number(input.dataset.indice) - 1;
        const existe = this.variants.some(
          (v) => v.available && v.options.every((opcion, i) => (i === indice ? opcion === input.value : opcion === valores[i]))
        );
        input.nextElementSibling?.classList.toggle('variantes__boton--agotado', !existe);
      });
      this.querySelectorAll('[data-valor-opcion]').forEach((span, i) => {
        if (valores[i]) span.textContent = valores[i];
      });
    }

    async onChange() {
      this.marcarNoDisponibles();
      filtrarGaleriaPorColor(colorElegido());
      const info = this.closest('[data-producto-info]');
      const variante = this.variantePara(this.seleccion);

      if (!variante) {
        const boton = info?.querySelector('[data-boton-anadir]');
        if (boton) {
          boton.setAttribute('aria-disabled', 'true');
          boton.textContent = theme.strings.variantUnavailable;
        }
        return;
      }

      const entrada = info?.querySelector('[data-input-variante]');
      if (entrada) entrada.value = variante.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variante.id);
      window.history.replaceState({}, '', url);

      /* Aviso para las apps (Pumper Bundles, packs, upsells…): la mayoría
         escuchan el cambio del campo «id» del formulario o estos eventos. */
      avisarCambioVariante(info, variante);

      try {
        const respuesta = await fetch(`${this.dataset.url}?variant=${variante.id}&section_id=${this.dataset.seccion}`);
        if (!respuesta.ok) return;
        const fresco = parseHTML(await respuesta.text()).querySelector('[data-producto-info]');
        if (!fresco || !info) return;

        /* Solo se cambian las piezas que dependen de la variante (precio,
           aviso de stock, botón y barra fija). Antes se repintaba toda la
           columna y eso borraba los bloques de apps, como el de combos de
           Pumper, que volvía a su estado inicial o desaparecía. */
        fresco.querySelectorAll('[data-actualizar]').forEach((nueva) => {
          const actual = info.querySelector(`[data-actualizar="${nueva.dataset.actualizar}"]`);
          if (actual) actual.replaceWith(nueva);
        });

        /* El botón del tema no se sustituye (una app puede haberlo escondido):
           solo se le cambia el texto y si se puede pulsar. */
        const botonNuevo = fresco.querySelector('[data-boton-anadir]');
        const botonActual = info.querySelector('[data-boton-anadir]');
        if (botonNuevo && botonActual) {
          botonActual.textContent = botonNuevo.textContent;
          if (botonNuevo.hasAttribute('aria-disabled')) botonActual.setAttribute('aria-disabled', 'true');
          else botonActual.removeAttribute('aria-disabled');
        }

        /* La barra fija nueva entra en lugar de la que hay en el <body>. */
        const barraNueva = fresco.querySelector('[data-barra-compra]');
        if (barraNueva) {
          info.querySelectorAll('[data-barra-compra]').forEach((b) => b.remove());
          info.appendChild(barraNueva);
          actualizarBarraCompra();
        }
        filtrarGaleriaPorColor(colorElegido());
      } catch (error) {
        /* Si falla la red, el formulario envía igualmente la variante marcada. */
      }
    }
  }
  if (!customElements.get('variant-selector')) customElements.define('variant-selector', VariantSelector);

  /* ---------- Filtros y ordenación ---------- */
  const zonaFacetas = document.querySelector('[data-facetas-zona]');
  if (zonaFacetas) {
    const form = zonaFacetas.querySelector('[data-facetas]');
    const resultados = document.querySelector('[data-resultados]');
    /* En la página de colección no hay sección que recargar (la plantilla es
       Liquid), así que los filtros navegan de forma normal. */
    const seccionId = resultados?.id.replace('ResultadosProductos-', '');
    const recargaCompleta = seccionId === 'coleccion';
    const escritorio = window.matchMedia('(min-width: 990px)');

    const sincronizarModo = () => {
      if (escritorio.matches) {
        form.removeAttribute('inert');
        form.classList.remove('is-abierto');
        document.body.classList.remove('bloqueo-scroll');
      } else if (!form.classList.contains('is-abierto')) {
        form.setAttribute('inert', '');
      }
    };
    sincronizarModo();
    escritorio.addEventListener('change', sincronizarModo);

    zonaFacetas.addEventListener('click', (event) => {
      const abrir = event.target.closest('[data-facetas-abrir]');
      if (abrir) {
        abrirPanel(form, abrir);
        abrir.setAttribute('aria-expanded', 'true');
        return;
      }
      const cerrar = event.target.closest('[data-facetas-cerrar]');
      const aplicar = event.target.closest('[data-facetas-aplicar]');
      if (cerrar || (aplicar && !escritorio.matches)) {
        if (cerrar) event.preventDefault();
        cerrarPanel(form);
        zonaFacetas.querySelector('[data-facetas-abrir]')?.setAttribute('aria-expanded', 'false');
      }
    });

    const aplicar = async (url, empujarHistorial = true) => {
      if (!resultados || !seccionId || recargaCompleta) {
        window.location.href = url;
        return;
      }
      zonaFacetas.classList.add('is-cargando');
      try {
        const respuesta = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${seccionId}`);
        if (!respuesta.ok) throw new Error('fetch');
        const doc = parseHTML(await respuesta.text());
        const nuevosResultados = doc.querySelector('[data-resultados]');
        const nuevaZona = doc.querySelector('[data-facetas-zona]');
        const abierto = form.classList.contains('is-abierto');

        if (nuevosResultados) document.querySelector('[data-resultados]').replaceWith(nuevosResultados);
        if (nuevaZona) {
          zonaFacetas.replaceWith(nuevaZona);
          if (abierto) {
            const nuevoForm = nuevaZona.querySelector('[data-facetas]');
            nuevoForm?.removeAttribute('inert');
            nuevoForm?.classList.add('is-abierto');
          }
        }
        if (empujarHistorial) window.history.pushState({ facetas: true }, '', url);
        document.querySelector('[data-resultados]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } catch (error) {
        window.location.href = url;
      }
    };

    const urlDesdeForm = () => {
      const params = new URLSearchParams();
      for (const [clave, valor] of new FormData(form).entries()) {
        if (String(valor).trim() !== '') params.append(clave, valor);
      }
      const orden = zonaFacetas.querySelector('[data-orden]');
      if (orden && orden.value) {
        params.delete('sort_by');
        params.append('sort_by', orden.value);
      }
      const cadena = params.toString();
      return cadena ? `${form.action}?${cadena}` : form.action;
    };

    let temporizador;
    zonaFacetas.addEventListener('change', (event) => {
      if (!event.target.closest('[data-facetas]') && !event.target.matches('[data-orden]')) return;
      clearTimeout(temporizador);
      temporizador = setTimeout(() => aplicar(urlDesdeForm()), event.target.type === 'number' ? 600 : 0);
    });

    zonaFacetas.addEventListener('submit', (event) => {
      event.preventDefault();
      aplicar(urlDesdeForm());
    });

    document.addEventListener('click', (event) => {
      const enlace = event.target.closest('.facetas-activas a, [data-limpiar-facetas], .paginacion a');
      if (!enlace || !document.querySelector('[data-facetas-zona]')) return;
      event.preventDefault();
      aplicar(enlace.href);
    });

    window.addEventListener('popstate', () => aplicar(window.location.href, false));
  }


  /* ---------- Marquesina: duplicar hasta que no queden huecos ---------- */
  document.querySelectorAll('.aviso__marquesina').forEach((marquesina) => {
    const pistas = [...marquesina.querySelectorAll('.aviso__pista')];
    if (!pistas.length) return;
    const original = pistas[0];

    const rellenar = () => {
      /* Deja solo la pista original y vuelve a duplicar según el ancho real. */
      pistas.slice(1).forEach((p) => p.remove());
      marquesina.querySelectorAll('.aviso__pista--copia').forEach((p) => p.remove());

      const anchoPista = original.scrollWidth;
      if (!anchoPista) return;

      /* Tantas copias como hagan falta para cubrir dos pantallas. */
      const copias = Math.max(1, Math.ceil((window.innerWidth * 2) / anchoPista));
      for (let i = 0; i < copias; i += 1) {
        const copia = original.cloneNode(true);
        copia.classList.add('aviso__pista--copia');
        copia.setAttribute('aria-hidden', 'true');
        copia.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        marquesina.appendChild(copia);
      }
      marquesina.style.setProperty('--aviso-recorrido', `${anchoPista}px`);
    };

    rellenar();
    let temporizador;
    window.addEventListener('resize', () => {
      clearTimeout(temporizador);
      temporizador = setTimeout(rellenar, 200);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rellenar);
  });

  /* ---------- Alto de la barra de aviso fija (para que la cabecera no la tape) ---------- */
  const aviso = document.querySelector('[data-aviso].aviso--fija');
  if (aviso) {
    const escritorio = window.matchMedia('(min-width: 990px)');
    const medir = () => {
      const alto = escritorio.matches ? 0 : aviso.offsetHeight;
      document.documentElement.style.setProperty('--aviso-alto', `${alto}px`);
    };
    medir();
    escritorio.addEventListener('change', medir);
    if ('ResizeObserver' in window) new ResizeObserver(medir).observe(aviso);
    else window.addEventListener('resize', medir);
  }

  /* ---------- Reseñas del carrito: rotación suave ---------- */
  const rotarResenas = (contenedor) => {
    const fichas = [...contenedor.querySelectorAll('.resena')];
    if (fichas.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let indice = 0;
    contenedor.__intervalo = setInterval(() => {
      fichas[indice].hidden = true;
      indice = (indice + 1) % fichas.length;
      fichas[indice].hidden = false;
    }, 6000);
  };
  document.querySelectorAll('[data-resenas]').forEach(rotarResenas);

  /* ---------- Ventana de descuento ---------- */
  const popup = document.querySelector('[data-popup]');
  if (popup) {
    const clave = popup.dataset.clave;
    const enEditor = popup.dataset.modoEditor === 'true';

    const silenciado = () => {
      try {
        const hasta = Number(window.localStorage.getItem(clave) || 0);
        return hasta > Date.now();
      } catch (error) {
        return false;
      }
    };

    const silenciar = () => {
      try {
        const dias = Number(popup.dataset.dias) || 30;
        window.localStorage.setItem(clave, String(Date.now() + dias * 86400000));
      } catch (error) {
        /* Si el navegador bloquea el almacenamiento, la ventana volverá a salir. */
      }
    };

    const cerrarPopup = (recordar = true) => {
      popup.classList.remove('is-abierto');
      if (!enEditor) popup.setAttribute('inert', '');
      document.body.classList.remove('bloqueo-scroll');
      if (recordar) silenciar();
      document.removeEventListener('keydown', popup.__onKey);
    };

    const abrirPopup = () => {
      popup.removeAttribute('inert');
      popup.classList.add('is-abierto');
      document.body.classList.add('bloqueo-scroll');
      const caja = popup.querySelector('[role="dialog"]');
      requestAnimationFrame(() => caja?.focus());
      popup.__onKey = (event) => {
        if (event.key === 'Escape') cerrarPopup();
        if (event.key !== 'Tab' || !caja) return;
        const items = [...caja.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === caja)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', popup.__onKey);
    };

    popup.addEventListener('click', (event) => {
      if (event.target.closest('[data-popup-cerrar]')) cerrarPopup();
    });

    const yaSuscrita = popup.querySelector('.popup__exito');
    if (yaSuscrita) {
      silenciar();
      abrirPopup();
    } else if (enEditor) {
      abrirPopup();
    } else if (!silenciado()) {
      setTimeout(abrirPopup, (Number(popup.dataset.retraso) || 0) * 1000);
    }
  }

  /* ---------- Menú de escritorio: un desplegable abierto a la vez ---------- */
  const megas = [...document.querySelectorAll('[data-mega]')];
  const closeMegas = (except) => megas.forEach((d) => { if (d !== except) d.removeAttribute('open'); });
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  megas.forEach((details) => {
    details.addEventListener('toggle', () => { if (details.open) closeMegas(details); });
    const item = details.closest('.nav__item');
    let timer;
    item?.addEventListener('mouseenter', () => {
      if (!canHover.matches) return;
      clearTimeout(timer);
      closeMegas(details);
      details.setAttribute('open', '');
    });
    item?.addEventListener('mouseleave', () => {
      if (!canHover.matches) return;
      timer = setTimeout(() => details.removeAttribute('open'), 150);
    });
    details.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.open) {
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      }
    });
  });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-mega]')) closeMegas();
  });

  /* ---------- Menú móvil ---------- */
  const mobileMenu = document.querySelector('[data-menu-movil]');
  if (mobileMenu) {
    const summary = mobileMenu.querySelector('summary');
    const closeMobile = () => {
      mobileMenu.removeAttribute('open');
      document.body.classList.remove('bloqueo-scroll');
      summary?.focus();
    };
    mobileMenu.addEventListener('toggle', () => {
      document.body.classList.toggle('bloqueo-scroll', mobileMenu.open);
      if (mobileMenu.open) mobileMenu.querySelector('.menu-movil__panel a, .menu-movil__panel summary')?.focus();
    });
    mobileMenu.addEventListener('click', (event) => {
      if (event.target.closest('[data-menu-movil-cerrar]')) closeMobile();
      else if (event.target === mobileMenu) closeMobile();
    });
    mobileMenu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileMenu.open) closeMobile();
    });
  }
})();
